import * as AdaptiveCards from 'adaptivecards';
import OutlookHostConfig from '../store/schema/OutlookHostConfig';

/* Note: adaptive card host configs are kept in sync with jsmvvm design here:
   //depot/Chicago/sources/Dev/Owa/src/Client/Connectors/js/src/index.js
*/

const DEFAULT_CARD_MAXIUMUM_WIDTH = '640px';

export function getAdaptiveCardHostConfig(): OutlookHostConfig {
    const hostConfig: OutlookHostConfig = new AdaptiveCards.HostConfig({
        choiceSetInputValueSeparator: ',',
        spacing: {
            small: 8,
            default: 16,
            medium: 16,
            large: 24,
            extraLarge: 32,
            padding: 16,
        },
        separator: {
            lineThickness: 1,
            lineColor: '#e1e1e1',
        },
        supportsInteractivity: true,
        fontFamily: 'Segoe UI, sans-serif',
        fontSizes: {
            small: 12,
            default: 14,
            medium: 16,
            large: 18,
            extraLarge: 20,
        },
        fontWeights: {
            lighter: 200,
            default: 400,
            bolder: 600,
        },
        containerStyles: {
            default: {
                highlightBackgroundColor: '#f0f0f0',
                highlightForegroundColor: '#000000',
                backgroundColor: '#FFFFFF',
                foregroundColors: {
                    default: {
                        default: '#212121',
                        subtle: '#8e8e93',
                    },
                    dark: {
                        default: '#1f1f1f',
                        subtle: '#707070',
                    },
                    light: {
                        default: '#707070',
                        subtle: '#8e8e93',
                    },
                    accent: {
                        default: '#0078d4',
                        subtle: '#69AFE5',
                    },
                    attention: {
                        default: '#E4252B',
                        subtle: '#F17a7D',
                    },
                    good: {
                        default: '#0A8533',
                        subtle: '#5BA975',
                    },
                    warning: {
                        default: '#FFA800',
                        subtle: '#F2BE4D',
                    },
                },
            },
            emphasis: {
                backgroundColor: '#F8F8F8',
                foregroundColors: {
                    default: {
                        default: '#212121',
                        subtle: '#8e8e93',
                    },
                    dark: {
                        default: '#1f1f1f',
                        subtle: '#707070',
                    },
                    light: {
                        default: '#707070',
                        subtle: '#8e8e93',
                    },
                    accent: {
                        default: '#0078d4',
                        subtle: '#69AFE5',
                    },
                    good: {
                        default: '#0a8031',
                        subtle: '#5BA975',
                    },
                    warning: {
                        default: '#FFA800',
                        subtle: '#F2BE4D',
                    },
                    attention: {
                        default: '#DA1b21',
                        subtle: '#F17a7d',
                    },
                },
            },
        },
        imageSizes: {
            small: 40,
            medium: 160,
            large: 320,
        },
        actions: {
            maxActions: 5,
            spacing: AdaptiveCards.Spacing.Default,
            buttonSpacing: 16,
            showCard: {
                actionMode: AdaptiveCards.ShowCardActionMode.Inline,
                inlineTopMargin: 16,
            },
            actionsOrientation: AdaptiveCards.Orientation.Horizontal,
            actionAlignment: AdaptiveCards.ActionAlignment.Left,
            preExpandSingleShowCardAction: true,
        },
        adaptiveCard: {
            allowCustomStyle: true,
        },
        imageSet: {
            imageSize: AdaptiveCards.Size.Medium,
            maxImageHeight: 100,
        },
        factSet: {
            title: {
                color: AdaptiveCards.TextColor.Default,
                size: AdaptiveCards.TextSize.Default,
                isSubtle: true,
                weight: AdaptiveCards.TextWeight.Lighter,
                wrap: true,
                maxWidth: 150,
            },
            value: {
                color: AdaptiveCards.TextColor.Default,
                size: AdaptiveCards.TextSize.Default,
                isSubtle: false,
                weight: AdaptiveCards.TextWeight.Default,
                wrap: true,
            },
            spacing: 8,
        },
    });

    hostConfig.cardHorizontalAlignment = 'center';
    hostConfig.cardMaximumWidth = DEFAULT_CARD_MAXIUMUM_WIDTH;
    return hostConfig;
}

export function getAdaptiveCardCompactHostConfig(): OutlookHostConfig {
    const hostConfig: OutlookHostConfig = new AdaptiveCards.HostConfig({
        choiceSetInputValueSeparator: ',',
        spacing: {
            small: 10,
            default: 12,
            medium: 14,
            large: 16,
            extraLarge: 18,
            padding: 8,
        },
        separator: {
            lineThickness: 1,
            lineColor: '#19000000',
        },
        supportsInteractivity: true,
        fontFamily: 'wf_segoe-ui_normal, sans-serif',
        fontSizes: {
            small: 10,
            default: 12,
            medium: 14,
            large: 16,
            extraLarge: 18,
        },
        fontWeights: {
            lighter: 200,
            default: 400,
            bolder: 600,
        },
        containerStyles: {
            default: {
                highlightBackgroundColor: '#f0f0f0',
                highlightForegroundColor: '#000000',
                backgroundColor: '#EAEAEA',
                foregroundColors: {
                    default: {
                        default: '#333333',
                        subtle: '#EE333333',
                    },
                    dark: {
                        default: '#000000',
                        subtle: '#66000000',
                    },
                    light: {
                        default: '#FFFFFF',
                        subtle: '#33000000',
                    },
                    accent: {
                        default: '#2E89FC',
                        subtle: '#882E89FC',
                    },
                    attention: {
                        default: '#FFD800',
                        subtle: '#DDFFD800',
                    },
                    good: {
                        default: '#00FF00',
                        subtle: '#DD00FF00',
                    },
                    warning: {
                        default: '#FF0000',
                        subtle: '#DDFF0000',
                    },
                },
            },
            emphasis: {
                backgroundColor: '#E0E0E0',
                foregroundColors: {
                    default: {
                        default: '#333333',
                        subtle: '#EE333333',
                    },
                    dark: {
                        default: '#000000',
                        subtle: '#66000000',
                    },
                    light: {
                        default: '#FFFFFF',
                        subtle: '#33000000',
                    },
                    accent: {
                        default: '#2E89FC',
                        subtle: '#882E89FC',
                    },
                    attention: {
                        default: '#FFD800',
                        subtle: '#DDFFD800',
                    },
                    good: {
                        default: '#00FF00',
                        subtle: '#DD00FF00',
                    },
                    warning: {
                        default: '#FF0000',
                        subtle: '#DDFF0000',
                    },
                },
            },
        },
        imageSizes: {
            small: 16,
            medium: 32,
            large: 64,
        },
        actions: {
            maxActions: 5,
            spacing: AdaptiveCards.Spacing.Default,
            buttonSpacing: 20,
            showCard: {
                actionMode: AdaptiveCards.ShowCardActionMode.Inline,
                inlineTopMargin: 10,
            },
            actionsOrientation: AdaptiveCards.Orientation.Horizontal,
            actionAlignment: AdaptiveCards.ActionAlignment.Left,
            preExpandSingleShowCardAction: true,
        },
        adaptiveCard: {
            allowCustomStyle: true,
        },
        imageSet: {
            imageSize: AdaptiveCards.Size.Medium,
            maxImageHeight: 100,
        },
        factSet: {
            title: {
                color: AdaptiveCards.TextColor.Default,
                size: AdaptiveCards.TextSize.Default,
                isSubtle: false,
                weight: AdaptiveCards.TextWeight.Bolder,
                wrap: true,
                maxWidth: 150,
            },
            value: {
                color: AdaptiveCards.TextColor.Default,
                size: AdaptiveCards.TextSize.Default,
                isSubtle: false,
                weight: AdaptiveCards.TextWeight.Default,
                wrap: true,
            },
            spacing: 10,
        },
    });

    hostConfig.cardHorizontalAlignment = 'center';
    hostConfig.cardMaximumWidth = DEFAULT_CARD_MAXIUMUM_WIDTH;
    hostConfig.cssClassNamePrefix = 'compactMessageCard';
    return hostConfig;
}

export function getMessageCardHostConfig(): OutlookHostConfig {
    const hostConfig: OutlookHostConfig = new AdaptiveCards.HostConfig({
        supportsInteractivity: true,
        choiceSetInputValueSeparator: ';',
        fontFamily: 'Segoe UI, sans-serif',
        fontSizes: {
            small: 12,
            default: 14,
            medium: 17,
            large: 21,
            extraLarge: 26,
        },
        fontWeights: {
            lighter: 200,
            default: 400,
            bolder: 600,
        },
        imageSizes: {
            small: 40,
            medium: 80,
            large: 160,
        },
        containerStyles: {
            default: {
                foregroundColors: {
                    default: {
                        default: '#333333',
                        subtle: '#EE333333',
                    },
                    accent: {
                        default: '#2E89FC',
                        subtle: '#882E89FC',
                    },
                    good: {
                        default: '#54a254',
                        subtle: '#DD54a254',
                    },
                    warning: {
                        default: '#e69500',
                        subtle: '#DDe69500',
                    },
                    attention: {
                        default: '#cc3300',
                        subtle: '#DDcc3300',
                    },
                },
                backgroundColor: '#FFFFFF',
            },
            emphasis: {
                foregroundColors: {
                    default: {
                        default: '#333333',
                        subtle: '#EE333333',
                    },
                    accent: {
                        default: '#2E89FC',
                        subtle: '#882E89FC',
                    },
                    good: {
                        default: '#54a254',
                        subtle: '#DD54a254',
                    },
                    warning: {
                        default: '#e69500',
                        subtle: '#DDe69500',
                    },
                    attention: {
                        default: '#cc3300',
                        subtle: '#DDcc3300',
                    },
                },
                backgroundColor: '#08000000',
            },
        },
        spacing: {
            small: 3,
            default: 8,
            medium: 20,
            large: 30,
            extraLarge: 40,
            padding: 20,
        },
        separator: {
            lineThickness: 1,
            lineColor: '#EEEEEE',
        },
        actions: {
            maxActions: 5,
            spacing: 'Default',
            buttonSpacing: 10,
            showCard: {
                actionMode: 'Inline',
                inlineTopMargin: 16,
                style: 'emphasis',
            },
            preExpandSingleShowCardAction: true,
            actionsOrientation: 'Horizontal',
            actionAlignment: 'Left',
        },
        adaptiveCard: {
            allowCustomStyle: true,
        },
        imageSet: {
            imageSize: 'Medium',
            maxImageHeight: 'maxImageHeight',
        },
        factSet: {
            title: {
                size: 'Default',
                color: 'Default',
                isSubtle: false,
                weight: 'Bolder',
                warp: true,
            },
            value: {
                size: 'Default',
                color: 'Default',
                isSubtle: false,
                weight: 'Default',
                warp: true,
            },
            spacing: 10,
        },
    });

    return hostConfig;
}

export function getMessageCardCompactHostConfig(): OutlookHostConfig {
    const hostConfig: OutlookHostConfig = new AdaptiveCards.HostConfig({
        supportsInteractivity: true,
        choiceSetInputValueSeparator: ';',
        spacing: {
            small: 5,
            default: 5,
            medium: 5,
            large: 5,
            extraLarge: 5,
            padding: 5,
        },
        separator: {
            lineThickness: 1,
            lineColor: '#EEEEEE',
        },
        fontFamily: 'wf_segoe-ui_normal, sans-serif',
        fontSizes: {
            small: 10,
            normal: 12,
            medium: 14,
            large: 16,
            extraLarge: 18,
        },
        fontWeights: {
            lighter: 200,
            normal: 400,
            bolder: 600,
        },
        containerStyles: {
            default: {
                backgroundColor: '#eaeaea',
                foregroundColors: {
                    default: { default: '#767676', subtle: '#EE767676' },
                    accent: { default: '#2E89FC', subtle: '#882E89FC' },
                    attention: { default: '#FFD800', subtle: '#DDFFD800' },
                    good: { default: '#00FF00', subtle: '#00DD00' },
                    warning: { default: '#FF0000', subtle: '#DD0000' },
                },
            },
            emphasis: {
                backgroundColor: '#F8F8F8',
                foregroundColors: {
                    default: { default: '#767676', subtle: '#EE767676' },
                    accent: { default: '#2E89FC', subtle: '#882E89FC' },
                    attention: { default: '#FFD800', subtle: '#DDFFD800' },
                    good: { default: '#00FF00', subtle: '#00DD00' },
                    warning: { default: '#FF0000', subtle: '#DD0000' },
                },
            },
        },
        imageSizes: {
            small: 16,
            medium: 32,
            large: 64,
        },
        actions: {
            maxActions: 5,
            buttonSpacing: 20,
            spacing: AdaptiveCards.Spacing.Default,
            showCard: {
                actionMode: AdaptiveCards.ShowCardActionMode.Inline,
                inlineTopMargin: 16,
                style: 'emphasis',
            },
            preExpandSingleShowCardAction: true,
            actionsOrientation: AdaptiveCards.Orientation.Horizontal,
            actionAlignment: AdaptiveCards.ActionAlignment.Left,
        },
        adaptiveCard: {
            allowCustomStyle: true,
        },
        imageSet: {
            imageSize: AdaptiveCards.Size.Medium,
            maxImageHeight: 100,
        },
        factSet: {
            title: {
                color: AdaptiveCards.TextColor.Default,
                size: AdaptiveCards.TextSize.Default,
                isSubtle: false,
                weight: AdaptiveCards.TextWeight.Bolder,
                wrap: true,
            },
            value: {
                color: AdaptiveCards.TextColor.Default,
                size: AdaptiveCards.TextSize.Default,
                isSubtle: false,
                weight: AdaptiveCards.TextWeight.Default,
                wrap: true,
            },
            spacing: 5,
        },
    });

    hostConfig.cssClassNamePrefix = 'compactMessageCard';
    return hostConfig;
}
